import React from 'react';
import { motion } from 'framer-motion';

const FirstClientGuaranteedPage = () => {
  return (
    <div className="first-client-page">
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{
            textAlign: 'center',
            marginBottom: '4rem',
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
            borderRadius: '20px',
            padding: '3rem',
            border: '1px solid rgba(255, 193, 7, 0.2)'
          }}
        >
          <motion.h1
            style={{
              fontSize: '3rem',
              fontWeight: '800',
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: '1.2'
            }}
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            You're one paying client away from changing everything. But the "tech" is killing your dream.
          </motion.h1>
        </motion.section>
        {/* Video Embed */}
<div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', margin: '2rem 0' }}>
  <iframe 
    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
    src="https://www.youtube.com/embed/ITemn_tRqOM?autoplay=1&mute=1&loop=1&playlist=ITemn_tRqOM&controls=1&modestbranding=1"
    frameBorder="0"
    allow="autoplay; encrypted-media"
    allowFullScreen>
  </iframe>
</div>

        {/* Part 1: The Hook */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '15px',
            padding: '2.5rem',
            borderLeft: '4px solid #ff6b6b'
          }}>
            <p style={{
              fontSize: '1.3rem',
              lineHeight: '1.8',
              color: '#e0e0e0',
              marginBottom: '0'
            }}>
              Let me guess. You have a soul-crushing day job, but you know you're meant for more. You have a skill people would pay for, and the dream is to build a side-income that eventually buys your freedom.
            </p>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              So you started. You listened to the gurus. And you walked right into the trap.
            </p>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              You bought a dozen tools that promised simplicity but delivered a spaghetti-mess of complexity. You tried to build a landing page, connect an email list, create a "freebie"... and now you're drowning in a sea of tabs, tutorials, and technical jargon.
            </p>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              You've spent weeks, maybe months, 'getting set up.' But you're no closer to getting paid. In fact, you're further away, because now you're frustrated, overwhelmed, and starting to believe the lie that maybe you're just not 'techy' enough to make this work.
            </p>

            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 142, 83, 0.1) 100%)',
              borderRadius: '10px',
              padding: '1.5rem',
              marginTop: '2rem',
              border: '1px solid rgba(255, 107, 107, 0.2)'
            }}>
              <p style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#ff6b6b',
                margin: '0',
                textAlign: 'center'
              }}>
                This isn't a tech problem. It's a focus problem. You're working on tasks that don't make you money.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Part 2: The Problem & Agitation */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <h2 style={{
            fontSize: '2.2rem',
            fontWeight: '700',
            color: '#ff6b6b',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            The Real Problem
          </h2>

          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '15px',
            padding: '2.5rem',
            marginBottom: '2rem'
          }}>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              You think your problem is that you don't know which button to click. But the real problem is that you are focusing on being a 'systems administrator' instead of a business owner.
            </p>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '0'
            }}>
              You are trying to build a complex rocket ship when all you need is a reliable car to get you to your first destination: <strong style={{ color: '#ffc107' }}>Your first paying client.</strong>
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
            borderRadius: '15px',
            padding: '2.5rem',
            border: '1px solid rgba(255, 193, 7, 0.2)'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#ffc107',
              marginBottom: '1.5rem'
            }}>
              The Cost of Inaction
            </h3>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              Every week you spend 'tinkering' with your tech stack is another week you're not making money. It's another week your dream fades. Let's do the math.
            </p>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '0'
            }}>
              Your goal isn't just to 'start a business.' It's to buy back your time and your life. If you stay on this path, in six months, you'll have a perfectly organized folder of unused tools ... and zero clients. You will have given up. <strong style={{ color: '#ff6b6b' }}>The cost isn't €399. The cost is your entire future.</strong>
            </p>
          </div>
        </motion.section>

        {/* Part 3: The Solution */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(100, 255, 218, 0.1) 0%, rgba(0, 255, 136, 0.1) 100%)',
            borderRadius: '20px',
            padding: '3rem',
            border: '1px solid rgba(100, 255, 218, 0.2)',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              color: '#64ffda',
              marginBottom: '2rem'
            }}>
              Introducing: The "First Client, Guaranteed" Sprint
            </h2>

            <p style={{
              fontSize: '1.3rem',
              lineHeight: '1.7',
              color: '#e0e0e0',
              marginBottom: '1.5rem'
            }}>
              This is not a course. This is not 'coaching.' This is a 30-day, done-with-you implementation sprint with one single, obsessive focus: <strong style={{ color: '#64ffda' }}>Getting you your first paying client.</strong>
            </p>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '1.5rem'
            }}>
              We are going to rip out all the complexity. We will ignore 99% of the things you could do and focus only on the 3-5 critical actions that lead directly to a payment notification in your inbox.
            </p>

            <p style={{
              fontSize: '1.2rem',
              fontWeight: '600',
              color: '#64ffda',
              marginBottom: '0'
            }}>
              We will build the shortest possible path from where you are now to getting paid.
            </p>
          </div>
        </motion.section>

        {/* Part 4: The Offer Stack */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <h2 style={{
            fontSize: '2.2rem',
            fontWeight: '700',
            color: '#ffc107',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            Here's exactly how we will get you there:
          </h2>

          <div style={{ display: 'grid', gap: '2rem', marginBottom: '3rem' }}>
            {/* Offer Item 1 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '2rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  color: '#64ffda',
                  margin: '0'
                }}>
                  1. The 'Irresistible Offer' Blueprint Session
                </h3>
                <span style={{
                  background: '#ffc107',
                  color: '#000',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  Value: €500
                </span>
              </div>
              <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                color: '#b0b0b0',
                margin: '0'
              }}>
                Before we touch any tech, we'll spend 90 minutes 1-on-1 forging your raw skill into an irresistible, high-ticket offer that people actually want to buy. You'll leave this session with a crystal-clear offer you can sell for €500-€2,000, immediately making the investment in this sprint trivial.
              </p>
            </div>

            {/* Offer Item 2 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '2rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  color: '#64ffda',
                  margin: '0'
                }}>
                  2. The 'SaaS Slayer' Tech-Stack Setup
                </h3>
                <span style={{
                  background: '#ffc107',
                  color: '#000',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  Value: €750
                </span>
              </div>
              <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                color: '#b0b0b0',
                margin: '0'
              }}>
                Stop the clicking. I will personally guide you, step-by-step, to set up your entire, simplified tech stack. Landing page, email provider, booking system. No confusion, no tutorials. In 2 hours, you'll have a fully functional client-acquisition machine, ready to go.
              </p>
            </div>

            {/* Offer Item 3 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '2rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  color: '#64ffda',
                  margin: '0'
                }}>
                  3. The 'Fill-in-the-Blank' Landing Page Copy
                </h3>
                <span style={{
                  background: '#ffc107',
                  color: '#000',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  Value: €450
                </span>
              </div>
              <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                color: '#b0b0b0',
                margin: '0'
              }}>
                You're not a copywriter, so don't try to be. I will give you my proven, high-converting landing page templates. You will not write from a blank page. You will fill in the blanks, and within an hour, you'll have a sales page that is better than what 99% of your competition struggles to create in a month.
              </p>
            </div>

            {/* Offer Item 4 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '2rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  color: '#64ffda',
                  margin: '0'
                }}>
                  4. The 'First Client' Acquisition Plan
                </h3>
                <span style={{
                  background: '#ffc107',
                  color: '#000',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  Value: €300
                </span>
              </div>
              <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                color: '#b0b0b0',
                margin: '0'
              }}>
                I'll show you exactly where to find your first potential clients on platforms like Reddit and YouTube and give you the exact scripts to use to get them from 'interested' to a booked call in your calendar.
              </p>
            </div>
          </div>

          {/* Price Summary */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
            borderRadius: '15px',
            padding: '2.5rem',
            border: '2px solid rgba(255, 193, 7, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{
                fontSize: '1.3rem',
                color: '#b0b0b0',
                margin: '0 0 0.5rem 0'
              }}>
                Total Value: <span style={{ color: '#ffc107', fontSize: '1.5rem', fontWeight: '700' }}>€2,000</span>
              </p>
              <p style={{
                fontSize: '1.5rem',
                color: '#e0e0e0',
                fontWeight: '700',
                margin: '0'
              }}>
                You Pay Today: <span style={{ color: '#ffc107', fontSize: '2rem' }}>€997</span>
              </p>
            </div>

            <p style={{
              fontSize: '1.2rem',
              color: '#b0b0b0',
              fontWeight: '600',
              margin: '0'
            }}>
              You get a €2,000 system, designed to land you a €1,000 client, for less than €1,000. The math speaks for itself.
            </p>
          </div>
        </motion.section>

        {/* Part 5: The Guarantee */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(100, 255, 218, 0.1) 100%)',
            borderRadius: '20px',
            padding: '3rem',
            border: '2px solid rgba(0, 255, 136, 0.3)',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: '800',
              color: '#00ff88',
              marginBottom: '2rem'
            }}>
              The 'First Client, Guaranteed' Guarantee
            </h2>

            <p style={{
              fontSize: '1.3rem',
              lineHeight: '1.7',
              color: '#e0e0e0',
              marginBottom: '1.5rem'
            }}>
              My promise is in the name of the sprint. We will work together for 30 days to implement the system. If you do the work and you do not land your first paying client within those 30 days, I will work with you for free until you do. Period.
            </p>

            <p style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#00ff88',
              margin: '0'
            }}>
              You either get the result you paid for, or you get my time for free. The risk is 100% on me.
            </p>
          </div>
        </motion.section>

        {/* Part 6: Call To Action */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '4rem' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 142, 83, 0.1) 100%)',
            borderRadius: '20px',
            padding: '3rem',
            border: '1px solid rgba(255, 107, 107, 0.2)',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#b0b0b0',
              marginBottom: '2rem'
            }}>
              This is an intensive, hands-on sprint. Because of that, I can only take on <strong style={{ color: '#ff6b6b' }}>5 new clients per month</strong> to guarantee results. I have <strong style={{ color: '#ffc107' }}>3 spots left</strong> for this month.
            </p>

            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              color: '#ff6b6b',
              marginBottom: '2rem'
            }}>
              If you are done tinkering and ready to get your first client, here's what to do next:
            </h3>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              padding: '2rem',
              marginBottom: '3rem',
              textAlign: 'left'
            }}>
              <ol style={{
                fontSize: '1.2rem',
                lineHeight: '1.8',
                color: '#b0b0b0',
                paddingLeft: '1.5rem'
              }}>
                <li style={{ marginBottom: '1rem' }}>Click the button below.</li>
                <li style={{ marginBottom: '1rem' }}>Book a quick, 15-minute 'Clarity Call' on my TidyCal page.</li>
                <li style={{ marginBottom: '0' }}>On that call, we will verify this is the right fit for you. There is no hard sell. If it's a fit, you're in. If not, we part ways.</li>
              </ol>
            </div>

            <p style={{
              fontSize: '1.1rem',
              color: '#b0b0b0',
              marginBottom: '3rem',
              fontStyle: 'italic'
            }}>
              This call is to ensure I can actually help you. I only accept clients I am 100% confident I can get results for.
            </p>

            <motion.a
              href="https://tidycal.com/mathiasdeppe/clarity-call"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                fontSize: '1.4rem',
                fontWeight: '700',
                padding: '1.5rem 3rem',
                background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                color: '#000',
                textDecoration: 'none',
                borderRadius: '50px',
                boxShadow: '0 10px 30px rgba(255, 193, 7, 0.4)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 15px 40px rgba(255, 193, 7, 0.6)',
                y: -2
              }}
              whileTap={{ scale: 0.98 }}
            >
              🚀 Book Your Clarity Call & Secure Your Spot
            </motion.a>
          </div>
        </motion.section>

      </div>
    </div>
  );
};

export default FirstClientGuaranteedPage;